import { MenuItem, Skeleton } from "@chakra-ui/react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { NeedReauthenticateError, useMfaApi } from "src/hooks/useMfaApi";
import { isNonEmptyString } from "src/services/predicates";

type Props = {
  person: {
    person_id: string;
    active: boolean;
  };
};

export const ResetMFAMenuItem: React.FC<Props> = ({ person }) => {
  const mfaApi = useMfaApi();
  const toast = useAvelaToast();

  const dialog = useConfirmationDialog({
    header: "Reset MFA?",
    body: "This will remove their current multi-factor authentication setup. They’ll be prompted to set up MFA again the next time they sign in.",
    confirmButton: {
      label: "Yes, reset MFA",
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const reauthenticate = React.useCallback(async () => {
    const returnTo = new URL(window.location.href);
    returnTo.searchParams.set("resetMFA", "true");
    await mfaApi.reauthenticate(returnTo.href);
  }, [mfaApi]);

  const resetMFA = React.useCallback(async () => {
    try {
      if (!(await mfaApi.hasMfaIdToken())) {
        await reauthenticate();
        return;
      }

      if (
        !(await dialog.confirm({
          onConfirm: (helpers) => {
            helpers.setButton({
              isLoading: true,
              loadingText: "Resetting MFA...",
              label: "",
            });
          },
        }))
      ) {
        return;
      }

      const mfaToken = await mfaApi.getMfaIdToken();
      await FSR.resetTeamMemberMfa({
        body: { mfaToken },
        path: { personId: person.person_id },
      });

      dialog.helpers.close();
      toast({ title: "MFA reset successfully", status: "success" });
    } catch (error) {
      dialog.helpers.close();
      if (error instanceof NeedReauthenticateError) {
        try {
          await reauthenticate();
        } catch (redirectError) {
          console.error(redirectError);
          toast.genericError();
        }
        return;
      }

      console.error(error);
      const message = (error as any)?.error?.message;
      if (isNonEmptyString(message)) {
        toast.error({ title: "Error resetting MFA", description: message });
      } else {
        toast.genericError();
      }
    }
  }, [dialog, mfaApi, person.person_id, reauthenticate, toast]);

  React.useEffect(() => {
    if (searchParams.get("resetMFA") === "true") {
      searchParams.delete("resetMFA");
      setSearchParams(searchParams, { replace: true });
      resetMFA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!person.active) {
    return null;
  }

  return (
    <>
      <RemoteDataView
        remoteData={mfaApi.isMfaEnabledRemoteData}
        loading={<Skeleton height="2rem" />}
        error={() => null}
        reloading={null}
      >
        {(enabled) =>
          enabled ? <MenuItem onClick={resetMFA}>Reset MFA</MenuItem> : null
        }
      </RemoteDataView>
      {dialog.confirmationDialog}
    </>
  );
};
